import React, { useState, useEffect } from "react";
import {
  Form,
  Button,
  Row,
  Col,
  Card,
  Alert,
  Tabs,
  Tab,
  InputGroup,
  Badge,
} from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { message } from "antd";
import {
  useGetServiceByIdQuery,
  useUpdateServiceMutation,
} from "../../../redux/api/ServicesApi/servicesApi";
import {
  FaPlus,
  FaTrash,
  FaSave,
  FaArrowLeft,
  FaLayerGroup,
  FaInfoCircle,
  FaListUl,
  FaTools,
  FaRocket,
  FaEdit,
  FaImage,
} from "react-icons/fa";
import "./Services.css";
import LoaderSpinner from "../../../Components/LoaderSpinner";
import DeleteModel from "../../../Components/DeleteModel";

const EditService = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: serviceData, isLoading: isFetching } =
    useGetServiceByIdQuery(id);
  const [updateService, { isLoading: isUpdating }] = useUpdateServiceMutation();

  const [errors, setErrors] = useState({});
  const [activeTab, setActiveTab] = useState("general");

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfig, setDeleteConfig] = useState({
    field: "",
    index: -1,
    label: "",
  });

  const [formData, setFormData] = useState({
    service_slug: "",
    service_title: "",
    service_subtitle: "",
    service_description: "",
    service_menu_description: "",
    service_menu_path: "",
    button_text_1: "",
    button_link_1: "",
    button_text_2: "",
    button_link_2: "",
    why_this_service_title: "",
    why_this_service_subtitle: "",
    why_this_service_description: "",
    why_this_service_highlightText: "",
    dev_process_title: "",
    dev_process_subtitle: "",
    dev_process_description: "",
    why_choose_title: "",
    headerImage: "",
    techstack_title: "",
    techstack_description: "",
    tools_title: "",
    cta_title: "",
    cta_description: "",
    defaultText: "",
    hoverText: "",
    status: true,
    display_order: 0,
    benefits: [],
    deliverables: [],
    faqs: [],
    process: [],
    whyChoose: [],
    tools: [],
    meta_title: "",
    meta_keyword: "",
    meta_description: "",
  });

  // Load service data when fetched
  useEffect(() => {
    if (serviceData) {
      setFormData({
        ...serviceData,
        // Map child arrays from backend objects to the form structure
        benefits: (serviceData.benefits || []).map((b) => ({
          benefit_text: b.benefit_text || "",
          benefit_icon: b.benefit_icon || "",
        })),
        deliverables: (serviceData.deliverables || []).map((d) => ({
          deliverables_title: d.deliverables_title || "",
          deliverables_description: d.deliverables_description || "",
          deliverables_order: d.deliverables_order || 0,
        })),
        faqs: (serviceData.faqs || []).map((f) => ({
          faq_question: f.faq_question || "",
          faq_answer: f.faq_answer || "",
        })),
        process: (serviceData.process || []).map((p) => ({
          process_title: p.process_title || "",
          process_subtitle: p.process_subtitle || "",
          process_description_one: p.process_description_one || "",
          process_description_two: p.process_description_two || "",
          process_description_three: p.process_description_three || "",
          process_description_four: p.process_description_four || "",
          process_description_five: p.process_description_five || "",
        })),
        whyChoose: (serviceData.whyChoose || []).map((w) => ({
          why_choose_title: w.why_choose_title || "",
          why_choose_description: w.why_choose_description || "",
          why_choose_image: w.why_choose_image || "",
        })),
        tools: (serviceData.tools || []).map((t) => ({
          tool_image: t.tool_image || "",
        })),
        meta_title: serviceData.meta_title || "",
        meta_keyword: serviceData.meta_keyword || "",
        meta_description: serviceData.meta_description || "",
      });
    }
  }, [serviceData]);

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    if (type === "file") {
      setFormData((prev) => ({ ...prev, [name]: files[0] }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleFileChange = (e, field, index = null, subfield = null) => {
    const file = e.target.files[0];
    if (file) {
      if (index !== null && subfield) {
        handleArrayChange(field, index, file, subfield);
      } else {
        setFormData((prev) => ({ ...prev, [field]: file }));
      }
    }
  };

  const getPreviewUrl = (file) => {
    if (!file) return "";
    if (typeof file === "string") return file;
    try {
      return URL.createObjectURL(file);
    } catch (e) {
      return "";
    }
  };

  // Helper for array manipulation
  const handleArrayChange = (field, index, value, subfield = null) => {
    setFormData((prev) => {
      const newArray = [...prev[field]];
      if (subfield) {
        newArray[index] = { ...newArray[index], [subfield]: value };
      } else {
        newArray[index] = value;
      }
      return { ...prev, [field]: newArray };
    });
  };

  const addArrayItem = (field, defaultValue) => {
    setFormData((prev) => ({
      ...prev,
      [field]: [...prev[field], defaultValue],
    }));
  };

  const removeArrayItem = (field, index) => {
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index),
    }));
  };

  const confirmRemoveItem = (field, index, label) => {
    setDeleteConfig({ field, index, label });
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteConfig({ field: "", index: -1, label: "" });
  };

  const handleDeleteConfirm = () => {
    removeArrayItem(deleteConfig.field, deleteConfig.index);
    handleCloseDeleteModal();
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.service_title?.trim())
      newErrors.service_title = "Service title is required";
    if (!formData.service_slug?.trim())
      newErrors.service_slug = "Service slug is required";
    if (!formData.service_menu_description?.trim())
      newErrors.service_menu_description = "Menu description is required";
    if (!formData.service_menu_path?.trim())
      newErrors.service_menu_path = "Menu path is required";
    if (!formData.cta_title?.trim())
      newErrors.cta_title = "CTA title is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    const data = new FormData();

    // Append standard fields
    Object.keys(formData).forEach((key) => {
      if (
        ![
          "benefits",
          "deliverables",
          "faqs",
          "process",
          "whyChoose",
          "headerImage",
          "tools", // Added tools to exclusion
          "id",
          "createdAt",
          "updatedAt",
        ].includes(key)
      ) {
        data.append(key, formData[key]);
      }
    });

    const activeBenefits = formData.benefits.filter((b) =>
      b.benefit_text?.trim(),
    );
    const activeWhyChoose = formData.whyChoose.filter((w) =>
      w.why_choose_title?.trim(),
    );
    const activeTools = formData.tools.filter((t) => t.tool_image); // Filter tools that have an image

    // We send arrays as JSON strings
    // If an item has an image as a File, we set it to null in the JSON and send it separately
    data.append(
      "benefits",
      JSON.stringify(
        activeBenefits.map((b) => ({
          ...b,
          benefit_icon:
            typeof b.benefit_icon === "string" ? b.benefit_icon : null,
        })),
      ),
    );
    data.append(
      "whyChoose",
      JSON.stringify(
        activeWhyChoose.map((w) => ({
          ...w,
          why_choose_image:
            typeof w.why_choose_image === "string" ? w.why_choose_image : null,
        })),
      ),
    );
    data.append(
      "deliverables",
      JSON.stringify(
        formData.deliverables.filter(
          (d) =>
            d.deliverables_title?.trim() || d.deliverables_description?.trim(),
        ),
      ),
    );
    data.append(
      "faqs",
      JSON.stringify(
        formData.faqs.filter(
          (f) => f.faq_question?.trim() || f.faq_answer?.trim(),
        ),
      ),
    );
    data.append(
      "process",
      JSON.stringify(formData.process.filter((p) => p.process_title?.trim())),
    );
    data.append(
      "tools",
      JSON.stringify(
        activeTools.map((t) => ({
          ...t,
          tool_image: typeof t.tool_image === "string" ? t.tool_image : null,
        })),
      ),
    );

    // Append files
    if (formData.headerImage instanceof File) {
      data.append("headerImage", formData.headerImage);
    } else if (typeof formData.headerImage === "string") {
      data.append("headerImage", formData.headerImage);
    }

    activeBenefits.forEach((b, index) => {
      if (b.benefit_icon instanceof File) {
        data.append(`benefits[${index}]`, b.benefit_icon);
      }
    });

    activeWhyChoose.forEach((w, index) => {
      if (w.why_choose_image instanceof File) {
        data.append(`whyChoose[${index}]`, w.why_choose_image);
      }
    });

    activeTools.forEach((t, index) => {
      if (t.tool_image instanceof File) {
        data.append(`tools[${index}]`, t.tool_image);
      }
    });

    try {
      await updateService({ id, data }).unwrap();
      message.success("Service updated successfully!");
      setTimeout(() => navigate("/admin/services"), 2000);
    } catch (error) {
      message.error(
        error.data?.error || error.message || "Failed to update service",
      );
    }
  };

  if (isFetching) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "100vh" }}
      >
        <LoaderSpinner />
      </div>
    );
  }

  return (
    <div className="container mt-4 mb-5">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="fw-bold text-primary">
            <FaEdit className="me-2" size={24} />
            {serviceData?.service_title}
          </h2>
          <p className="text-muted">
            Modify the details of the existing service.
          </p>
        </div>
        <Button
          variant="outline-secondary"
          className="d-flex align-items-center gap-2 shadow-sm"
          onClick={() => navigate("/admin/services")}
        >
          <FaArrowLeft /> Back to Services
        </Button>
      </div>

      <Form onSubmit={handleSubmit}>
        <Card className="border-0 overflow-hidden">
          <Card.Header className="bg-white p-0">
            <Tabs
              activeKey={activeTab}
              onSelect={(k) => setActiveTab(k)}
              className="admin-tabs border-bottom-0"
              fill
            >
              <Tab
                eventKey="general"
                title={
                  <span>
                    <FaLayerGroup className="me-2" />
                    General Info
                  </span>
                }
              />
              <Tab
                eventKey="why-us"
                title={
                  <span>
                    <FaInfoCircle className="me-2" />
                    Why Us
                  </span>
                }
              />
              <Tab
                eventKey="process"
                title={
                  <span>
                    <FaRocket className="me-2" />
                    Process
                  </span>
                }
              />
              <Tab
                eventKey="lists"
                title={
                  <span>
                    <FaListUl className="me-2" />
                    Deliverables
                  </span>
                }
              />
              <Tab
                eventKey="extra"
                title={
                  <span>
                    <FaTools className="me-2" />
                    Stack & Extra
                  </span>
                }
              />
              <Tab
                eventKey="seo"
                title={
                  <span>
                    <FaRocket className="me-2" />
                    SEO Details
                  </span>
                }
              />
            </Tabs>
          </Card.Header>

          <Card.Body className="p-4">
            {/* Tab 1: General Info */}
            {activeTab === "general" && (
              <div className="fade-in">
                <h5 className="mb-4 text-secondary border-start border-4 border-primary ps-3">
                  Common Information
                </h5>
                <Row className="g-3">
                  <Col md={7}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">
                        Service Title <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="service_title"
                        value={formData.service_title}
                        onChange={handleChange}
                        isInvalid={!!errors.service_title}
                        placeholder="e.g. Web Development"
                        className="py-2"
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.service_title}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={5}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">
                        Service Slug
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="service_slug"
                        value={formData.service_slug}
                        onChange={handleChange}
                        isInvalid={!!errors.service_slug}
                        placeholder="service-url-slug"
                        className="py-2"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">Subtitle</Form.Label>
                      <Form.Control
                        type="text"
                        name="service_subtitle"
                        value={formData.service_subtitle}
                        onChange={handleChange}
                        placeholder="Short catchy subtitle"
                        className="py-2"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">
                        Service Description
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        name="service_description"
                        value={formData.service_description}
                        onChange={handleChange}
                        placeholder="Describe what this service is about..."
                      />
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">
                        Menu Description <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        name="service_menu_description"
                        value={formData.service_menu_description}
                        onChange={handleChange}
                        isInvalid={!!errors.service_menu_description}
                        placeholder="Short description for the dropdown menu..."
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.service_menu_description}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">
                        Menu Path <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="service_menu_path"
                        value={formData.service_menu_path}
                        onChange={handleChange}
                        isInvalid={!!errors.service_menu_path}
                        placeholder="e.g. /services/web-development"
                        className="py-2"
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.service_menu_path}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">
                        Default Card Text
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="defaultText"
                        value={formData.defaultText}
                        onChange={handleChange}
                        placeholder="Text shown by default"
                        className="py-2"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">
                        Hover Card Text
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="hoverText"
                        value={formData.hoverText}
                        onChange={handleChange}
                        placeholder="Text shown on hover"
                        className="py-2"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">
                        Header Background Image
                      </Form.Label>
                      <div className="custom-file-container">
                        <input
                          type="file"
                          id="header-image-input-edit"
                          accept="image/*"
                          onChange={(e) => handleFileChange(e, "headerImage")}
                          hidden
                        />
                        <label
                          htmlFor="header-image-input-edit"
                          className="d-flex align-items-center gap-2 border rounded-3 p-1 cursor-pointer bg-white shadow-sm"
                          style={{ minHeight: "45px" }}
                        >
                          <div className="btn btn-primary btn-sm rounded-pill px-3 ms-1">
                            Choose File
                          </div>
                          <span className="text-muted small text-truncate px-2 flex-grow-1">
                            {formData.headerImage
                              ? formData.headerImage.name ||
                                (typeof formData.headerImage === "string"
                                  ? formData.headerImage.split("/").pop()
                                  : "File selected")
                              : "No file chosen"}
                          </span>
                          <div className="pe-3 text-primary">
                            <FaImage />
                          </div>
                        </label>
                      </div>
                      {formData.headerImage && (
                        <div className="mt-2 text-center bg-light p-2 rounded border border-dashed">
                          <img
                            src={getPreviewUrl(formData.headerImage)}
                            alt="Header Preview"
                            style={{
                              maxWidth: "100%",
                              maxHeight: "100px",
                              objectFit: "contain",
                            }}
                          />
                        </div>
                      )}
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">
                        Display Order
                      </Form.Label>
                      <Form.Control
                        type="number"
                        name="display_order"
                        value={formData.display_order}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3} className="d-flex align-items-end">
                    <Form.Group className="mb-3">
                      <Form.Check
                        type="switch"
                        id="status-switch"
                        label="Active Status"
                        name="status"
                        checked={formData.status}
                        onChange={handleChange}
                        className="fw-bold"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <h5 className="mt-4 mb-4 text-secondary border-start border-4 border-info ps-3">
                  Button Settings
                </h5>
                <Row className="g-3">
                  <Col md={6}>
                    <Card className="bg-light border-0">
                      <Card.Body>
                        <Form.Label className="fw-bold small text-uppercase">
                          Primary Button
                        </Form.Label>
                        <Form.Control
                          className="mb-2"
                          type="text"
                          name="button_text_1"
                          value={formData.button_text_1}
                          onChange={handleChange}
                          placeholder="Text (e.g. Contact Us)"
                        />
                        <Form.Control
                          type="text"
                          name="button_link_1"
                          value={formData.button_link_1}
                          onChange={handleChange}
                          placeholder="Link (/contact)"
                        />
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={6}>
                    <Card className="bg-light border-0">
                      <Card.Body>
                        <Form.Label className="fw-bold small text-uppercase">
                          Secondary Button
                        </Form.Label>
                        <Form.Control
                          className="mb-2"
                          type="text"
                          name="button_text_2"
                          value={formData.button_text_2}
                          onChange={handleChange}
                          placeholder="Text (e.g. Portfolio)"
                        />
                        <Form.Control
                          type="text"
                          name="button_link_2"
                          value={formData.button_link_2}
                          onChange={handleChange}
                          placeholder="Link (/portfolio)"
                        />
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </div>
            )}

            {/* Tab: SEO Details */}
            {activeTab === "seo" && (
              <div className="fade-in">
                <h5 className="mb-4 text-secondary border-start border-4 border-primary ps-3">
                  SEO Meta Details
                </h5>
                <Row className="g-3">
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">
                        Meta Title
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="meta_title"
                        value={formData.meta_title}
                        onChange={handleChange}
                        placeholder="Enter SEO meta title..."
                        className="py-2"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">
                        Meta Keywords
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        name="meta_keyword"
                        value={formData.meta_keyword}
                        onChange={handleChange}
                        placeholder="keyword1, keyword2, keyword3..."
                      />
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">
                        Meta Description
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={4}
                        name="meta_description"
                        value={formData.meta_description}
                        onChange={handleChange}
                        placeholder="Enter SEO meta description..."
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </div>
            )}

            {/* Tab 2: Why This Service */}
            {activeTab === "why-us" && (
              <div className="fade-in">
                <h5 className="mb-4 text-secondary border-start border-4 border-warning ps-3">
                  Why This Service Section
                </h5>
                <Row className="g-3">
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">
                        Section Title
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="why_this_service_title"
                        value={formData.why_this_service_title}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">
                        Section Subtitle
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="why_this_service_subtitle"
                        value={formData.why_this_service_subtitle}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">
                        Section Description
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        name="why_this_service_description"
                        value={formData.why_this_service_description}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col lg={12}>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h5 className="mb-0 text-secondary border-start border-4 border-primary ps-3">
                        Benefits
                      </h5>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() =>
                          addArrayItem("benefits", {
                            benefit_text: "",
                            benefit_icon: "",
                          })
                        }
                      >
                        <FaPlus size={12} />
                      </Button>
                    </div>
                    {formData.benefits.map((item, index) => (
                      <Card
                        className="mb-2 bg-light border-0 shadow-sm"
                        key={index}
                      >
                        <Card.Body className="p-2">
                          <Form.Control
                            className="mb-1"
                            size="sm"
                            value={item.benefit_text}
                            onChange={(e) =>
                              handleArrayChange(
                                "benefits",
                                index,
                                e.target.value,
                                "benefit_text",
                              )
                            }
                            placeholder="Benefit text"
                          />
                          <div className="custom-file-container mt-1">
                            <input
                              type="file"
                              id={`benefit-icon-edit-${index}`}
                              accept="image/*"
                              onChange={(e) =>
                                handleFileChange(
                                  e,
                                  "benefits",
                                  index,
                                  "benefit_icon",
                                )
                              }
                              hidden
                            />
                            <label
                              htmlFor={`benefit-icon-edit-${index}`}
                              className="d-flex align-items-center gap-2 border rounded-3 p-1 cursor-pointer bg-white shadow-sm w-100"
                              style={{ minHeight: "38px" }}
                            >
                              <div
                                className="btn btn-primary btn-xs rounded-pill px-2 ms-1"
                                style={{ fontSize: "10px" }}
                              >
                                Choose
                              </div>
                              <span
                                className="text-muted small text-truncate px-1 flex-grow-1"
                                style={{ fontSize: "11px" }}
                              >
                                {item.benefit_icon
                                  ? item.benefit_icon.name ||
                                    (typeof item.benefit_icon === "string"
                                      ? item.benefit_icon.split("/").pop()
                                      : "File selected")
                                  : "No icon"}
                              </span>
                              <FaImage
                                className="text-primary me-2"
                                size={14}
                              />
                            </label>
                          </div>
                          <div className="d-flex justify-content-end mt-1">
                            <Button
                              variant="link"
                              className="text-danger p-0"
                              style={{
                                fontSize: "10px",
                                textDecoration: "none",
                              }}
                              onClick={() =>
                                confirmRemoveItem(
                                  "benefits",
                                  index,
                                  item.benefit_text || "this benefit",
                                )
                              }
                            >
                              <FaTrash size={10} className="me-1" /> Remove
                            </Button>
                          </div>
                          {item.benefit_icon && (
                            <div className="mt-1">
                              <img
                                src={getPreviewUrl(item.benefit_icon)}
                                alt="Icon Preview"
                                style={{
                                  width: "30px",
                                  height: "30px",
                                  objectFit: "contain",
                                }}
                              />
                            </div>
                          )}
                        </Card.Body>
                      </Card>
                    ))}
                  </Col>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">
                        Highlight Text
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="why_this_service_highlightText"
                        value={formData.why_this_service_highlightText}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">
                        Why choose title
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="why_choose_title"
                        value={formData.why_choose_title}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <hr className="my-4" />
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h5 className="mb-0 text-secondary border-start border-4 border-danger ps-3">
                        Why Choose Points
                      </h5>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() =>
                          addArrayItem("whyChoose", {
                            why_choose_title: "",
                            why_choose_description: "",
                            why_choose_image: "",
                          })
                        }
                      >
                        <FaPlus className="me-1" /> Add Point
                      </Button>
                    </div>
                    {formData.whyChoose.map((item, index) => (
                      <Card className="mb-3 border-light shadow-sm" key={index}>
                        <Card.Body>
                          <Row className="g-2">
                            <Col md={4}>
                              <Form.Control
                                className="mb-2"
                                placeholder="Title"
                                value={item.why_choose_title}
                                onChange={(e) =>
                                  handleArrayChange(
                                    "whyChoose",
                                    index,
                                    e.target.value,
                                    "why_choose_title",
                                  )
                                }
                              />
                              <div className="custom-file-container mt-1">
                                <input
                                  type="file"
                                  id={`why-choose-image-edit-${index}`}
                                  accept="image/*"
                                  onChange={(e) =>
                                    handleFileChange(
                                      e,
                                      "whyChoose",
                                      index,
                                      "why_choose_image",
                                    )
                                  }
                                  hidden
                                />
                                <label
                                  htmlFor={`why-choose-image-edit-${index}`}
                                  className="d-flex align-items-center gap-2 border rounded-3 p-1 cursor-pointer bg-white shadow-sm w-100"
                                  style={{ minHeight: "38px" }}
                                >
                                  <div
                                    className="btn btn-primary btn-xs rounded-pill px-2 ms-1"
                                    style={{ fontSize: "10px" }}
                                  >
                                    Choose
                                  </div>
                                  <span
                                    className="text-muted small text-truncate px-1 flex-grow-1"
                                    style={{ fontSize: "11px" }}
                                  >
                                    {item.why_choose_image
                                      ? item.why_choose_image.name ||
                                        (typeof item.why_choose_image ===
                                        "string"
                                          ? item.why_choose_image
                                              .split("/")
                                              .pop()
                                          : "File selected")
                                      : "No image"}
                                  </span>
                                  <FaImage
                                    className="text-primary me-2"
                                    size={14}
                                  />
                                </label>
                              </div>
                              {item.why_choose_image && (
                                <div className="mt-1">
                                  <img
                                    src={getPreviewUrl(item.why_choose_image)}
                                    alt="Why Choose Preview"
                                    style={{
                                      width: "40px",
                                      height: "40px",
                                      objectFit: "contain",
                                    }}
                                  />
                                </div>
                              )}
                            </Col>
                            <Col md={7}>
                              <Form.Control
                                as="textarea"
                                rows={2}
                                placeholder="Brief Description"
                                value={item.why_choose_description}
                                onChange={(e) =>
                                  handleArrayChange(
                                    "whyChoose",
                                    index,
                                    e.target.value,
                                    "why_choose_description",
                                  )
                                }
                              />
                            </Col>
                            <Col md={1} className="text-end">
                              <Button
                                variant="link"
                                className="text-danger"
                                onClick={() =>
                                  confirmRemoveItem(
                                    "whyChoose",
                                    index,
                                    item.why_choose_title || "this point",
                                  )
                                }
                              >
                                <FaTrash />
                              </Button>
                            </Col>
                          </Row>
                        </Card.Body>
                      </Card>
                    ))}
                    {formData.whyChoose.length === 0 && (
                      <Alert
                        variant="light"
                        className="text-center py-4 border-dashed"
                      >
                        No reasons added yet. Click "Add Point" to start.
                      </Alert>
                    )}
                  </Col>
                </Row>
              </div>
            )}

            {/* Tab 3: Dev Process */}
            {activeTab === "process" && (
              <div className="fade-in">
                <h5 className="mb-4 text-secondary border-start border-4 border-dark ps-3">
                  Development Process Header
                </h5>
                <Row className="g-3">
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">
                        Process Title
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="dev_process_title"
                        value={formData.dev_process_title}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">
                        Process Subtitle
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="dev_process_subtitle"
                        value={formData.dev_process_subtitle}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">
                        Process Description
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="dev_process_description"
                        value={formData.dev_process_description}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <hr className="my-4" />
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0 text-secondary border-start border-4 border-success ps-3">
                    Process Steps
                  </h5>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() =>
                      addArrayItem("process", {
                        process_title: "",
                        process_subtitle: "",
                        process_description_one: "",
                        process_description_two: "",
                        process_description_three: "",
                        process_description_four: "",
                        process_description_five: "",
                      })
                    }
                  >
                    <FaPlus className="me-1" /> Add Step
                  </Button>
                </div>

                <div className="process-steps">
                  {formData.process.map((step, index) => (
                    <Card className="mb-3 border-light shadow-sm" key={index}>
                      <Card.Body>
                        <div className="d-flex justify-content-between mb-3">
                          <Badge bg="dark" className="px-3 py-2 rounded-pill">
                            Step {index + 1}
                          </Badge>
                          <Button
                            variant="link"
                            className="p-0 text-danger"
                            onClick={() =>
                              confirmRemoveItem(
                                "process",
                                index,
                                step.process_title || `Step ${index + 1}`,
                              )
                            }
                          >
                            <FaTrash />
                          </Button>
                        </div>
                        <Row className="g-3">
                          <Col md={6}>
                            <Form.Control
                              className="fw-bold"
                              placeholder="Process Title"
                              value={step.process_title}
                              onChange={(e) =>
                                handleArrayChange(
                                  "process",
                                  index,
                                  e.target.value,
                                  "process_title",
                                )
                              }
                            />
                          </Col>
                          <Col md={6}>
                            <Form.Control
                              placeholder="Process Subtitle"
                              value={step.process_subtitle}
                              onChange={(e) =>
                                handleArrayChange(
                                  "process",
                                  index,
                                  e.target.value,
                                  "process_subtitle",
                                )
                              }
                            />
                          </Col>
                          <Col md={12}>
                            <Form.Control
                              className="mb-2"
                              size="sm"
                              placeholder="Description Point 1"
                              value={step.process_description_one}
                              onChange={(e) =>
                                handleArrayChange(
                                  "process",
                                  index,
                                  e.target.value,
                                  "process_description_one",
                                )
                              }
                            />
                          </Col>
                          <Col md={12}>
                            <Form.Control
                              className="mb-2"
                              size="sm"
                              placeholder="Description Point 2"
                              value={step.process_description_two}
                              onChange={(e) =>
                                handleArrayChange(
                                  "process",
                                  index,
                                  e.target.value,
                                  "process_description_two",
                                )
                              }
                            />
                          </Col>
                          <Col md={12}>
                            <Form.Control
                              size="sm"
                              placeholder="Description Point 3"
                              value={step.process_description_three}
                              onChange={(e) =>
                                handleArrayChange(
                                  "process",
                                  index,
                                  e.target.value,
                                  "process_description_three",
                                )
                              }
                            />
                          </Col>
                          <Col md={12}>
                            <Form.Control
                              size="sm"
                              placeholder="Description Point 4"
                              value={step.process_description_four}
                              onChange={(e) =>
                                handleArrayChange(
                                  "process",
                                  index,
                                  e.target.value,
                                  "process_description_four",
                                )
                              }
                            />
                          </Col>
                          <Col md={12}>
                            <Form.Control
                              size="sm"
                              placeholder="Description Point 5"
                              value={step.process_description_five}
                              onChange={(e) =>
                                handleArrayChange(
                                  "process",
                                  index,
                                  e.target.value,
                                  "process_description_five",
                                )
                              }
                            />
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  ))}
                  {formData.process.length === 0 && (
                    <Alert
                      variant="light"
                      className="text-center py-4 border-dashed"
                    >
                      No process steps added yet.
                    </Alert>
                  )}
                </div>
              </div>
            )}

            {/* Tab 4: Lists */}
            {activeTab === "lists" && (
              <div className="fade-in">
                <Row className="g-4">
                  <Col lg={12}>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h5 className="mb-0 text-secondary border-start border-4 border-info ps-3">
                        Deliverables
                      </h5>
                      <Button
                        variant="outline-info"
                        size="sm"
                        onClick={() =>
                          addArrayItem("deliverables", {
                            deliverables_title: "",
                            deliverables_description: "",
                          })
                        }
                      >
                        <FaPlus size={12} />
                      </Button>
                    </div>
                    {formData.deliverables.map((item, index) => (
                      <Card
                        className="mb-2 bg-light border-0 shadow-sm"
                        key={index}
                      >
                        <Card.Body className="p-2">
                          <Form.Control
                            className="mb-1"
                            size="sm"
                            value={item.deliverables_title}
                            onChange={(e) =>
                              handleArrayChange(
                                "deliverables",
                                index,
                                e.target.value,
                                "deliverables_title",
                              )
                            }
                            placeholder="Title"
                          />
                          <InputGroup>
                            <Form.Control
                              size="sm"
                              value={item.deliverables_description}
                              onChange={(e) =>
                                handleArrayChange(
                                  "deliverables",
                                  index,
                                  e.target.value,
                                  "deliverables_description",
                                )
                              }
                              placeholder="Description"
                            />
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() =>
                                confirmRemoveItem(
                                  "deliverables",
                                  index,
                                  item.deliverables_title || "this deliverable",
                                )
                              }
                            >
                              <FaTrash size={12} />
                            </Button>
                          </InputGroup>
                        </Card.Body>
                      </Card>
                    ))}
                  </Col>
                </Row>
              </div>
            )}

            {/* Tab 5: Tech & FAQ */}
            {activeTab === "extra" && (
              <div className="fade-in">
                <h5 className="mb-4 text-secondary border-start border-4 border-indigo ps-3">
                  Stack & Extra
                </h5>
                <Row className="g-3">
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">
                        Tech Stack Title
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="techstack_title"
                        value={formData.techstack_title}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">
                        Tech Stack Description
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        name="techstack_description"
                        value={formData.techstack_description}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">
                        Tools Title
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="tools_title"
                        value={formData.tools_title}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>

                  {/* TOOLS SECTION */}
                  <Col md={12}>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="mb-0 fw-bold">Tools Images</h6>
                      <Button
                        variant="outline-dark"
                        size="sm"
                        onClick={() =>
                          addArrayItem("tools", {
                            tool_image: "",
                          })
                        }
                      >
                        <FaPlus className="me-1" /> Add Tool
                      </Button>
                    </div>
                    <Row xs={2} md={4} lg={6} className="g-3">
                      {formData.tools.map((item, index) => (
                        <Col key={index}>
                          <Card className="h-100 shadow-sm">
                            <Card.Body className="p-2 text-center position-relative">
                              <Button
                                variant="danger"
                                size="sm"
                                className="position-absolute top-0 end-0 m-1 p-0 rounded-circle"
                                style={{
                                  width: "20px",
                                  height: "20px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                                onClick={() =>
                                  confirmRemoveItem("tools", index, "this tool")
                                }
                              >
                                <FaTrash size={10} />
                              </Button>
                              <div
                                className="d-flex align-items-center justify-content-center bg-light rounded mb-2"
                                style={{ height: "60px" }}
                              >
                                {item.tool_image ? (
                                  <img
                                    src={getPreviewUrl(item.tool_image)}
                                    alt="Tool"
                                    style={{
                                      maxWidth: "100%",
                                      maxHeight: "100%",
                                      objectFit: "contain",
                                    }}
                                  />
                                ) : (
                                  <FaImage className="text-muted" size={20} />
                                )}
                              </div>
                              <div className="custom-file-container">
                                <input
                                  type="file"
                                  id={`tool-image-edit-${index}`}
                                  accept="image/*"
                                  onChange={(e) =>
                                    handleFileChange(
                                      e,
                                      "tools",
                                      index,
                                      "tool_image",
                                    )
                                  }
                                  hidden
                                />
                                <label
                                  htmlFor={`tool-image-edit-${index}`}
                                  className="btn btn-outline-primary btn-xs w-100 rounded-pill mt-1"
                                  style={{ fontSize: "10px" }}
                                >
                                  {item.tool_image
                                    ? "Change Image"
                                    : "Select Image"}
                                </label>
                                {item.tool_image && (
                                  <div
                                    className="text-muted x-small text-truncate mt-1 px-1"
                                    style={{ fontSize: "9px" }}
                                  >
                                    {item.tool_image.name ||
                                      (typeof item.tool_image === "string"
                                        ? item.tool_image.split("/").pop()
                                        : "Selected")}
                                  </div>
                                )}
                              </div>
                            </Card.Body>
                          </Card>
                        </Col>
                      ))}
                    </Row>
                  </Col>

                  <Col md={12}>
                    <div className="d-flex justify-content-between align-items-center mb-3 mt-4">
                      <h6 className="mb-0 fw-bold">
                        Frequently Asked Questions
                      </h6>
                    </div>
                    {formData.faqs.map((item, index) => (
                      <Card className="mb-2 border-light shadow-sm" key={index}>
                        <Card.Body className="p-3">
                          <div className="d-flex gap-2">
                            <div className="flex-grow-1">
                              <Form.Control
                                className="mb-2 fw-bold"
                                placeholder="Question"
                                value={item.faq_question}
                                onChange={(e) =>
                                  handleArrayChange(
                                    "faqs",
                                    index,
                                    e.target.value,
                                    "faq_question",
                                  )
                                }
                              />
                              <Form.Control
                                as="textarea"
                                rows={2}
                                placeholder="Answer"
                                value={item.faq_answer}
                                onChange={(e) =>
                                  handleArrayChange(
                                    "faqs",
                                    index,
                                    e.target.value,
                                    "faq_answer",
                                  )
                                }
                              />
                            </div>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              className="align-self-start"
                              onClick={() =>
                                confirmRemoveItem("faqs", index, "this FAQ")
                              }
                            >
                              <FaTrash />
                            </Button>
                          </div>
                        </Card.Body>
                      </Card>
                    ))}
                    {formData.faqs.length === 0 && (
                      <Alert
                        variant="light"
                        className="text-center py-4 border-dashed"
                      >
                        No FAQs added yet.{" "}
                      </Alert>
                    )}
                    <Button
                      variant="outline-dark"
                      size="sm"
                      className="float-end mt-2"
                      onClick={() =>
                        addArrayItem("faqs", {
                          faq_question: "",
                          faq_answer: "",
                        })
                      }
                    >
                      <FaPlus className="me-1" /> Add FAQ
                    </Button>
                  </Col>

                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">
                        CTA Title <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="cta_title"
                        value={formData.cta_title}
                        onChange={handleChange}
                        isInvalid={!!errors.cta_title}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.cta_title}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">
                        CTA Description
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        name="cta_description"
                        value={formData.cta_description}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </div>
            )}
          </Card.Body>

          <Card.Footer className="bg-white p-4 text-end">
            <Button
              type="button"
              variant="light"
              className="me-3 px-4 py-2 text-secondary"
              onClick={() => navigate("/admin/services")}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              className="px-5 py-2 shadow"
              disabled={isUpdating}
            >
              {isUpdating ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Updating...
                </>
              ) : (
                <>
                  <FaSave className="me-2" /> Update Service
                </>
              )}
            </Button>
          </Card.Footer>
        </Card>
      </Form>
      <DeleteModel
        DELETESTATE={showDeleteModal}
        ONCLICK={handleCloseDeleteModal}
        YES={handleDeleteConfirm}
        DELETETITLE="Confirm Deletion"
        DESCRIPTION={`Are you sure you want to delete "${deleteConfig.label}"`}
        loadTest="Deleting..."
      />
    </div>
  );
};

export default EditService;
